/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer, CircularProgress } from "@mui/material";
import { IServiceDeviceData, IdrawerCounts } from "../../store/api/devices/types";
import {
  drawerCountsSelector,
  isOpenDeviceDrawerSelector,
  selectedDeviceAlarmCountSelector,
  selectedDeviceAlarmsSelector,
  selectedDeviceSelector,
} from "../../store/api/devices/selectors";
import { isDasModel, prepareDeviceConfigDetail, prepareDrawerCounts } from "../../pages/Configuration/helpers";
import { IFullDeviceDetails } from "../../store/api/deviceConfig/types";
import { prepareActionProps } from "../Actions/ActionHelper";
import { deviceDrawerOptions } from "../Actions/ActionOptions";
import Actions from "../Actions/Actions";
import { useStoreDispatch } from "../../store/hooks";
import {
  setDrawerCounts,
  setOpenDeviceDrawer,
  setSelectedDevice,
  setSelectedDeviceAlarmCount,
  setSelectedDeviceAlarms,
} from "../../store/api/devices/reducers";
import { isLoadingStatusSelector } from "../../store/helpers/helperSelectors";
import { constStr } from "../../globals/constants";
import { setSelectedDeviceSiteGroup } from "../../store/filters/Nodes/reducer";
import { setSelectedNavListItems } from "../../store/helpers/helperReducers";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { initialState } from "../../store/api/devices/initialState";
import { resetKeyword } from "../../store/search/reducer";
import { useSummaryDisplay } from "../../pages/Configuration/Summary/hooks";
import { useGetAllNotesQuery, useGetGroupsQuery } from "../../store/api/devices/deviceApiSlice";
import Notes from "../../pages/DeviceDetail/Notes/Notes";
import { useGetDasRadioNodesQuery, useGetDasFeatureFlagsQuery } from "../../store/api/devices/deviceApiSlice";
import { resolveDeviceStatusLabel } from "../../utils/deviceStatus";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { titleCase, convertEpochToGMT, capitalizeWords } from "../../utils/helpers";
import { DATEFORMATS } from "../../globals/globals";
import { IEvent, IGROUPS } from "../../store/api/devices/types";
import TagLabel from "../Shared/Tag/TagLabel";
import { Icon } from "../Shared/Icon";
import { IDataGrid, IDataGridItem, ISummaryDataGridItem } from "../../pages/Configuration/types";
import StatusIcon from "../Shared/Status/StatusIcon";
import { getDeviceStatusIndicatorConfig } from "../helpers";

interface IDeviceDrawerProps {
  data?: IServiceDeviceData | IFullDeviceDetails;
  closedButtonClicked?: (arg: boolean) => void;
  callbackFun?: () => void;
}

const SEVERITY_ICON: Record<string, { name: string; className: string }> = {
  Critical: { name: "error", className: "text-destructive" },
  Major: { name: "error_outline", className: "text-warning" },
  Minor: { name: "warning", className: "text-yellow-500" },
  Warning: { name: "warning_amber", className: "text-yellow-400" },
};

function KpiCard({
  title,
  kpiValue,
  kpiIcon,
  subtext,
  loading,
}: {
  title: string;
  kpiValue: React.ReactNode;
  kpiIcon?: React.ReactNode;
  subtext?: string;
  loading?: boolean;
}) {
  return (
    <Card className="pt-2 px-4 pb-4">
      <CardTitle className="text-sm font-semibold mb-2">{title}</CardTitle>
      <div className="flex items-center gap-2">
        {kpiIcon}
        {loading ? (
          <CircularProgress size={20} color="primary" />
        ) : (
          <span className="text-sm font-semibold tabular-nums">{kpiValue}</span>
        )}
      </div>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </Card>
  );
}

function NameValueField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground">{label.toUpperCase() === "MANAGEMENT IP" ? "Management IP" : label.toUpperCase() === "KPI SYNC STATUS" ? "KPI Sync Status" : label}</span>
      <span className="font-medium truncate" title={label.toUpperCase() === "KPI SYNC STATUS" ? undefined : (value || "—")}>     
        {label.toUpperCase() === "KPI SYNC STATUS" ? (
          <StatusIcon status={value} isLabel={false} />
        ) : label.toUpperCase() === "CONFIGURATION STATUS" ? (
          (value?.trim().toLowerCase() === "na" ? "NA" : capitalizeWords(value)) || "—"
        ) : label.toUpperCase() === "CLUSTER ID" ? (
          capitalizeWords(value.toUpperCase()) || "—"
        ) : (
          value || "—"
        )}
      </span>
    </div>
  );
}

function DeviceStatusIndicator({
  isOffline,
  statusCode,
  operationMode,
}: {
  isOffline: boolean;
  statusCode?: number;
  operationMode?: string;
}) {
  const statusIndicatorConfig = getDeviceStatusIndicatorConfig(
    { isOffline, statusCode, operationMode },
    {
      offlineTextClassName: "text-foreground",
    }
  );

  return (
    <span className={statusIndicatorConfig.wrapperClassName}>
      <Icon name={statusIndicatorConfig.iconName} size={14} className={statusIndicatorConfig.iconClassName} />
      <span className="truncate">{statusIndicatorConfig.label}</span>
    </span>
  );
}

const DeviceDrawer: React.FC<IDeviceDrawerProps> = ({ data, closedButtonClicked, callbackFun }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const isLoadingStatus = useSelector(isLoadingStatusSelector);

  const drawerCounts: IdrawerCounts = useSelector(drawerCountsSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const totalAlarmsCount = useSelector(selectedDeviceAlarmCountSelector);
  const alarmsData = useSelector(selectedDeviceAlarmsSelector);
  const isOpenDeviceDrawer = useSelector(isOpenDeviceDrawerSelector);
  const isDas = isDasModel(selectedDevice.modelName);  
  const dasDeviceKey = data?.objectKey || selectedDevice?.objectKey;

  const { data: dasFeatureFlags } = useGetDasFeatureFlagsQuery(
    selectedDevice.modelName,
    { skip: !isDas }
  );
  const inventoryDiscoveryEnabled = isDas ? (dasFeatureFlags?.inventoryDiscovery ?? true) : true;

  const { data: dasRadioNodes = [], isLoading: isLoadingDasRadioNodes } = useGetDasRadioNodesQuery(
    { deviceKey: dasDeviceKey },
    {
      skip: !isDas || !isOpenDeviceDrawer || !dasDeviceKey || !inventoryDiscoveryEnabled,
      pollingInterval: 30000,
      refetchOnMountOrArgChange: 30,
    }
  );

  const dasRadioNodeCounts = React.useMemo(() => {
    const down = dasRadioNodes.reduce((count, item) => {
      return resolveDeviceStatusLabel(item?.status) === "Disconnected" ? count + 1 : count;
    }, 0);
    const up = Math.max(dasRadioNodes.length - down, 0);
    return { up, down };
  }, [dasRadioNodes]);
  
  const ActionBtnProp = false;
  const deviceDrawerRowData: IDataGrid = prepareDeviceConfigDetail(selectedDevice, drawerCounts?.uptime) || ({} as IDataGrid);
  const deviceDrawerRowHeader = isDas
    ? (inventoryDiscoveryEnabled ? [{ key: "RADIO NODES", downValue: dasRadioNodeCounts.down, upValue: dasRadioNodeCounts.up }] : [])
    : prepareDrawerCounts(drawerCounts,  selectedDevice?.nodeType || data?.nodeType) || [];
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const actionProps = prepareActionProps(data?.objectKey!, data?.name || "", data, deviceDrawerOptions, "deviceDrawer", ActionBtnProp, permissions, inventoryDiscoveryEnabled);
  useSummaryDisplay();
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const groups = useGetGroupsQuery();

  const { configurationStatus } = data || {};
  const isOfflineDevice = configurationStatus?.toLowerCase() === "offline";
  const { data: notesData = [] } = useGetAllNotesQuery({ deviceKey: data?.objectKey });
  const notesBottomRef = React.useRef<HTMLDivElement | null>(null);
  const notesSectionRef = React.useRef<HTMLDivElement | null>(null);
  const [notesAutoScrollEnabled, setNotesAutoScrollEnabled] = React.useState(false);
  const [detailsExpanded, setDetailsExpanded] = React.useState(false);
  const [showNotesInput, setShowNotesInput] = React.useState(false);

  const nodeType = isDasModel(deviceDetail.modelName) ? deviceDetail.modelName : deviceDetail.nodeType;

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }
    if (!open) {
      dispatch(setDrawerCounts(initialState.drawerCounts));
      dispatch(setSelectedDevice(initialState.selectedDevice));
      dispatch(setSelectedDeviceAlarms([]));
      dispatch(setSelectedDeviceAlarmCount(0));
    }
    dispatch(setOpenDeviceDrawer(open));
    closedButtonClicked && closedButtonClicked(open);
    callbackFun && callbackFun();
  };

  const navigateToDevice = (state?: { scrollToTab: string }) => {
    const opts = state ? { state } : undefined;
    if (location.pathname.includes("performance")) {
      navigate(`/device/${data?.objectKey}`, opts);
    } else if (location.pathname.includes("events") || location.pathname.includes("alarms") || location.pathname.includes("conditions")) {
      navigate(`../../device/${data?.objectKey}`, opts);
    } else if (location.pathname.includes("nodes")) {
      // For /nodes/devices, /nodes/map, etc. - use absolute path
      navigate(`/device/${data?.objectKey}`, opts);
    } else if (location.pathname.includes("administration")) {
      // For /administration/* routes - use absolute path
      navigate(`/device/${data?.objectKey}`, opts);
    } else {
      navigate(`../device/${data?.objectKey}`, opts);
    }
    if (selectedDevice?.siteGroupId.includes(constStr.ungroupedSiteId)) {
      dispatch(setSelectedDeviceSiteGroup(constStr.ungrouped));
    } else {
      dispatch(setSelectedDeviceSiteGroup(selectedDevice?.siteGroupId));
    }
    dispatch(setSelectedNavListItems("devices"));
    dispatch(setOpenDeviceDrawer(false));
    dispatch(resetKeyword());
  };

  const handleOnClick = async () => {
    navigateToDevice();
  };

  const handleSeeAll = (scrollToTab: string) => {
    navigateToDevice({ scrollToTab });
  };

  const handleOnPerformance = () => {
    navigate(`/device/${data?.objectKey}/performance`, { state: { deviceDetail } });
  };

  const allDataLoaded =
    !isLoadingStatus["isLoadingDeviceDetail"] &&
    !isLoadingStatus["isLoadingAlarms"] &&
    !isLoadingStatus["isLoadingCounts"];
  const isCountLoading = isDas ? isLoadingDasRadioNodes : isLoadingStatus["isLoadingCounts"];

  // Queue scroll — will fire once allDataLoaded becomes true
  const [pendingScroll, setPendingScroll] = React.useState(false);

  const handleOpenNotes = () => {
    setNotesAutoScrollEnabled(true);
    if (!allDataLoaded) {
      setPendingScroll(true);
      return;
    }
    if (notesSectionRef.current) {
      notesSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Execute pending scroll once all data is loaded
  React.useEffect(() => {
    if (pendingScroll && allDataLoaded) {
      setPendingScroll(false);
      requestAnimationFrame(() => {
        if (notesSectionRef.current) {
          notesSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  }, [pendingScroll, allDataLoaded]);

  React.useEffect(() => {
    if (isOpenDeviceDrawer) {
      setNotesAutoScrollEnabled(false);
      setDetailsExpanded(false);
      setShowNotesInput(false);
      setPendingScroll(false);
    }
  }, [isOpenDeviceDrawer]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const requestedDeviceKey = window.sessionStorage.getItem("openNotesForDevice");
    const isDeviceMatch = requestedDeviceKey && requestedDeviceKey === data?.objectKey;
    if (isOpenDeviceDrawer && isDeviceMatch && allDataLoaded) {
      const scrollTimeout = setTimeout(() => {
        if (notesSectionRef.current) {
          handleOpenNotes();
          window.sessionStorage.removeItem("openNotesForDevice");
        }
      }, 300);
      return () => clearTimeout(scrollTimeout);
    }
  }, [isOpenDeviceDrawer, data?.objectKey, allDataLoaded]);

  const ueCurrentSession = selectedDevice.nodeType === "SN" ? selectedDevice?.lteCurrentUESession : selectedDevice?.ngCurrentUESession;
  const ueMaxSession = selectedDevice.nodeType === "SN" ? selectedDevice?.lteMaxUESession : selectedDevice?.ngMaxUESession;

  const hasNotes = notesData.length > 0;
  const notesTooltip = hasNotes ? "View notes" : "Add note";
  //const isDas = data?.nodeType === "MA3000" || data?.nodeType === "MA6200" || data?.nodeType === "MA6000";

  /* Helper to resolve notification group name */
  const resolveGroupName = (groupKey: string): string => {
    if (!groupKey) return "System Default";
    const found = groups?.data?.find((g: IGROUPS) => g.key === groupKey);
    return found?.name || "System Default";
  };

  const SectionDivider = () => (
    <div className="my-8 h-px w-full rounded-full bg-gradient-to-r from-transparent via-border to-transparent" />
  );

  /* ─── Render a grid of name-value fields from IDataGridItem[] ─── */
  const renderFieldGrid = (items: (IDataGridItem | ISummaryDataGridItem)[]) => (
    <div className="grid grid-cols-3 gap-x-3 gap-y-6 text-sm">
      {items.map((item) => {
        if (item.key === "LABEL") {
          const labelValue = typeof item.value === "string" ? item.value : (item.value as string[]).join(",");
          return (
            <div className="flex flex-col gap-1" key={item.key}>
              <span className="text-muted-foreground">{titleCase(item.key)}</span>
              <TagLabel labels={labelValue ? labelValue.split(",") : []} maxVisible={3} />
            </div>
          );
        }
        if (item.key === "NOTIFICATIONS") {
          return (
            <NameValueField
              key={item.key}
              label={titleCase(item.key)}
              value={resolveGroupName(typeof item.value === "string" ? item.value : "")}
            />
          );
        }
        if (item.key === "PCI LOCK ENABLED") {
          return (
            <NameValueField
              key={item.key}
              label={titleCase(item.key)}
              value={titleCase(typeof item.value === "string" ? item.value : "")}
            />
          );
        }
        return (
          <NameValueField
            key={item.key}
            label={titleCase(item.key)}
            value={typeof item.value === "string" ? item.value : String(item.value ?? "")}
          />
        );
      })}
    </div>
  );

  return (
    <Drawer
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: "560px",
          maxWidth: "90vw",
          height: "100%",
          top: 0,
          backgroundColor: "var(--color-background)",
          borderLeft: "1px solid var(--color-border)",
          borderRadius: "10px 0 0 10px",
          boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
          overflow: "hidden",
        },
      }}
      ModalProps={{ disableEnforceFocus: true }}
      anchor="right"
      open={isOpenDeviceDrawer}
      onClose={toggleDrawer(false)}
    >
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-col h-full min-h-0 bg-background">
          {/* ─── Status color bar ─── */}
          {selectedDevice?.operationMode === "Maintenance" && (
            <div className="shrink-0 h-4 bg-warning rounded-tl-[10px]" />
          )}
          {selectedDevice?.operationMode !== "Maintenance" && data?.status === 19 && (
            <div className="shrink-0 h-4 bg-destructive rounded-tl-[10px]" />
          )}
          {/* ─── Header ─── */}
          <div className="relative pr-12 shrink-0 px-4 pt-2 pb-2">
            {/* Close button */}
            <button
              type="button"
              className="absolute right-2 top-2 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none"
              aria-label="Close"
              onClick={toggleDrawer(false) as any}
            >
              <Icon name="close" size={20} />
            </button>

            {/* Device name */}
            <h2
              className="text-xl font-semibold truncate pr-8 text-foreground"
              title={data?.name || ""}
            >
              {data?.name || "Unknown"}
            </h2>

            {/* Badges & status indicators */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="secondary" className="font-normal">
                {data?.nodeType}
              </Badge>
              <span className="text-sm text-muted-foreground">•</span>

              <DeviceStatusIndicator
                isOffline={!!isOfflineDevice}
                statusCode={data?.status}
                operationMode={selectedDevice?.operationMode}
              />

              {/* Notes icon */}
              <span className="text-sm text-muted-foreground">•</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center h-7 w-7 shrink-0 rounded-md bg-transparent border-0 p-0 focus-visible:outline-none"
                    aria-label={notesTooltip}
                    onClick={hasNotes ? handleOpenNotes : undefined}
                    style={{
                      color: "var(--device-drawer-notes-icon)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--device-drawer-notes-hover)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <Icon
                      name={hasNotes ? "note" : "note_add"}
                      size={18}
                      style={{ color: "var(--device-drawer-notes-icon)" }}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{notesTooltip}</TooltipContent>
              </Tooltip>
            </div>

            {/* Action buttons (matches AMS: Details, Performance, Action dropdown) */}
            <div className="flex items-center gap-2 pt-3">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleOnClick}
                disabled={isLoadingStatus["isLoadingDeviceDetail"]}
              >
                Details
              </Button>
              {!isDas && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleOnPerformance}
                  disabled={isLoadingStatus["isLoadingDeviceDetail"]}
                >
                  Performance
                </Button>
              )}
              {!!actionProps && <Actions actionProps={actionProps} disabled={isLoadingStatus["isLoadingDeviceDetail"]} size="small" />}
            </div>
          </div>

          {/* ─── Scrollable content ─── */}
          <div className="flex-1 min-h-0 overflow-auto px-4 pb-4 space-y-4">
            {/* ─── KPI Cards (matches AMS: 2-col for normal, 1-col for DAS) ─── */}
            <div className={`grid gap-3 ${isDas ? "grid-cols-1" : "grid-cols-2"}`}>
              <KpiCard
                title="Alarms"
                loading={isLoadingStatus["isLoadingAlarms"]}
                kpiValue={
                  <div className="flex items-center gap-3">
                    {/* Critical */}
                    <span className="inline-flex items-center gap-1">
                      <Icon name="error" size={16} className="shrink-0 text-destructive" />
                      <span className="tabular-nums">{selectedDevice?.criticalAlarmCount ?? 0}</span>
                    </span>
                    {/* Major */}
                    <span className="inline-flex items-center gap-1">
                      <Icon name="error_outline" size={16} className="shrink-0 text-warning" />
                      <span className="tabular-nums">{selectedDevice?.majorAlarmCount ?? 0}</span>
                    </span>
                    {/* Minor */}
                    <span className="inline-flex items-center gap-1">
                      <Icon name="warning" size={16} className="shrink-0 text-yellow-500" />
                      <span className="tabular-nums">
                        {Math.max(0, (totalAlarmsCount ?? 0) - (selectedDevice?.criticalAlarmCount ?? 0) - (selectedDevice?.majorAlarmCount ?? 0))}
                      </span>
                    </span>
                  </div>
                }
              />
              {!isDas && (
                <KpiCard
                  title="UE sessions"
                  loading={isLoadingStatus["isLoadingDeviceDetail"]}
                  kpiIcon={<Icon name="smartphone" size={16} className="text-muted-foreground" />}
                  kpiValue={ueCurrentSession || "—"}
                  subtext={ueMaxSession ? `${ueMaxSession} max` : undefined}
                />
              )}
              {!isDas && !selectedDevice?.pmCollected && (
                <KpiCard
                  title="KPI sync status"
                  loading={isLoadingStatus["isLoadingDeviceDetail"]}
                  kpiIcon={<Icon name="sync_problem" size={16} className="text-destructive" />}
                  kpiValue={<span className="tabular-nums">Error</span>}
                />
              )}
            </div>

            {/* ─── Main data card ─── */}
            {!isLoadingStatus["isLoadingDeviceDetail"] ? (
              <Card>
                <CardContent className="pt-6 space-y-8">
                  {/* ─── Counts row (Radio nodes / NR cells / X2 connections) ─── */}
                  {deviceDrawerRowHeader.length > 0 && (
                    <>
                      <div className={`grid gap-x-3 gap-y-6 text-sm ${isDas ? "grid-cols-1" : "grid-cols-3"}`}>
                        {deviceDrawerRowHeader.map((item) => (
                          <div className="flex flex-col gap-1.5" key={item.key}>
                            <span className="text-muted-foreground">{titleCase(item.key)}</span>
                            <div className="flex items-center gap-3">
                              <span className="font-medium tabular-nums inline-flex items-center gap-1.5">
                                <Icon name="arrow_upward" size={16} className="text-emerald-600 dark:text-emerald-500" />
                                {isCountLoading ? (
                                  <CircularProgress size={14} color="primary" />
                                ) : (
                                  item.upValue
                                )}
                              </span>
                              <span className="font-medium tabular-nums inline-flex items-center gap-1.5">
                                <Icon name="arrow_downward" size={16} className="text-destructive" />
                                {isCountLoading ? (
                                  <CircularProgress size={14} color="primary" />
                                ) : (
                                  item.downValue
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <SectionDivider />
                    </>
                  )}

                  {/* ─── Summary ─── */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Summary</h4>
                    {deviceDrawerRowData.summary && renderFieldGrid(deviceDrawerRowData.summary)}
                  </div>

                  <SectionDivider />

                  {/* ─── Status ─── */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Status</h4>
                    {deviceDrawerRowData.status && renderFieldGrid(deviceDrawerRowData.status)}
                  </div>

                  {/* ─── Expandable Details ─── */}
                  {detailsExpanded && (
                    <>
                      <SectionDivider />
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-foreground">Location</h4>
                        {deviceDrawerRowData.location && renderFieldGrid(deviceDrawerRowData.location)}
                      </div>

                      <SectionDivider />
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-foreground">Hardware</h4>
                        {deviceDrawerRowData.hardware && renderFieldGrid(deviceDrawerRowData.hardware)}
                      </div>

                      <SectionDivider />
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-foreground">Settings</h4>
                        {deviceDrawerRowData.settings && renderFieldGrid(deviceDrawerRowData.settings)}
                      </div>
                    </>
                  )}

                  {/* See more / Show less */}
                  <button
                    type="button"
                    className="h-7 text-xs w-full flex items-center justify-center gap-1 rounded-md bg-transparent hover:bg-transparent border-0 p-0 text-foreground hover:text-foreground/80 transition-colors"
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                  >
                    {detailsExpanded ? "Show less" : "See more"}
                    <Icon name={detailsExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} size={16} />
                  </button>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center min-h-[400px]">
                <CircularProgress size="4rem" color="primary" />
              </div>
            )}

            {/* ─── Alarms card ─── */}
            {!isLoadingStatus["isLoadingDeviceDetail"] && (
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    Alarms
                    <Badge variant="secondary" className="h-5 min-w-5 px-1.5 justify-center text-xs font-medium">
                      {totalAlarmsCount}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {isLoadingStatus["isLoadingAlarms"] ? (
                    <div className="flex items-center justify-center py-6">
                      <CircularProgress size={24} color="primary" />
                    </div>
                  ) : alarmsData.length > 0 ? (
                    <div className="overflow-hidden min-h-[200px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="h-8 px-2">Severity</TableHead>
                            <TableHead className="h-8 px-2">Timestamp</TableHead>
                            <TableHead className="h-8 px-2">Updated</TableHead>
                            <TableHead className="h-8 px-2">Source</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {alarmsData.slice(0, 5).map((alarm: IEvent, i: number) => {
                            const severity = alarm.severity || "Minor";
                            const cfg = SEVERITY_ICON[severity] || SEVERITY_ICON.Minor;
                            return (
                              <TableRow key={alarm.objectKey || i}>
                                <TableCell className="py-1.5 px-2">
                                  <span className="group/alarm inline-flex items-center gap-1.5 text-xs cursor-pointer text-left">
                                    <Icon name={cfg.name} size={14} className={`shrink-0 ${cfg.className}`} />
                                    <span className="group-hover/alarm:underline">{severity}</span>
                                    <Icon
                                      name="open_in_new"
                                      size={12}
                                      className="shrink-0 opacity-0 group-hover/alarm:opacity-70 transition-opacity text-muted-foreground"
                                    />
                                  </span>
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs tabular-nums">
                                  {alarm.occuredDateAndTimeMs
                                    ? convertEpochToGMT(alarm.occuredDateAndTimeMs, DATEFORMATS.TS)
                                    : "—"}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs tabular-nums">
                                  {alarm.retention
                                    ? convertEpochToGMT(alarm.retention, DATEFORMATS.TS)
                                    : "—"}
                                </TableCell>
                                <TableCell className="py-1.5 px-2 text-xs truncate max-w-[24ch]">
                                  {data?.name || "—"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No alarms</p>
                  )}
                  <button
                    type="button"
                    className="h-7 text-xs w-full flex items-center justify-center gap-1 rounded-md bg-transparent hover:bg-transparent border-0 p-0 text-foreground hover:text-foreground/80 transition-colors"
                    onClick={() => handleSeeAll("alarms")}
                  >
                    See all
                    <Icon name="arrow_forward" size={14} />
                  </button>
                </CardContent>
              </Card>
            )}

            {/* ─── Notes card ─── */}
            <Card ref={notesSectionRef}>
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  Notes
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 justify-center text-xs font-medium">
                    {notesData.length > 9 ? "9+" : notesData.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {hasNotes || showNotesInput ? (
                  <div className="max-h-[300px] overflow-y-auto">
                    <Notes enableAutoScroll={notesAutoScrollEnabled} />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6">
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => { setShowNotesInput(true); setNotesAutoScrollEnabled(true); }}>
                      <Icon name="note_add" size={18} />
                      Add note
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
      <div ref={notesBottomRef} />
    </Drawer>
  );
};

export default DeviceDrawer;